import {
  Button,
  Theme,
  Tooltip,
  WithStyles,
  createStyles,
  withStyles,
} from '@material-ui/core';

import { RiEmotionLine } from '@remixicon/react';

import { getTypeOrNull } from '@spotify/confidence-graphql';

import { EmojiPickerPopover } from '../EmojiPickerPopover';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      flexWrap: 'wrap',
    },
    emoji: {
      fontSize: '1rem',
      lineHeight: 1,
    },
    compactEmoji: {
      fontSize: '0.875rem',
    },
  });

export interface EmojiReaction {
  key: string;
  value?: {
    emoji?: string | null;
    reactionCount?: number | null;
    identities: Array<
      | { __typename: 'IamV1Identity'; name: string; displayName: string }
      | { __typename: 'Error' }
    >;
  } | null;
}

export interface EmojiReactionsProps extends WithStyles<typeof styles> {
  emojiReactions: EmojiReaction[];
  userReactedEmojis?: Set<string>;
  compact?: boolean;
  onToggleReaction?: (emoji: string, isAdding: boolean) => void;
}

const EmojiReactions = ({
  classes,
  emojiReactions,
  userReactedEmojis = new Set(),
  compact = true,
  onToggleReaction,
}: EmojiReactionsProps) => {
  const handleReactionToggle = (emoji: string) => {
    onToggleReaction?.(emoji, !userReactedEmojis.has(emoji));
  };

  const handleEmojiSelect = (emoji: string) => {
    // If user already reacted with this emoji, do nothing
    if (userReactedEmojis.has(emoji)) return;
    onToggleReaction?.(emoji, true);
  };

  return (
    <div className={classes.root}>
      {emojiReactions.map(reaction => {
        const emoji = reaction.value?.emoji ?? reaction.key;
        const count = reaction.value?.reactionCount ?? 0;
        const isActive = userReactedEmojis.has(emoji);

        const identityNames = (reaction.value?.identities ?? [])
          .map(identity => getTypeOrNull(identity)?.displayName)
          .filter(Boolean)
          .join(', ');

        return (
          <Tooltip title={identityNames || reaction.key} key={reaction.key}>
            <Button
              variant={isActive ? 'outlined' : 'text'}
              size="small"
              startIcon={
                <span
                  className={compact ? classes.compactEmoji : classes.emoji}
                >
                  {emoji}
                </span>
              }
              onClick={() => handleReactionToggle(emoji)}
              data-track-id="button.reaction"
              data-track-action="update"
            >
              {count}
            </Button>
          </Tooltip>
        );
      })}
      <EmojiPickerPopover
        icon={<RiEmotionLine />}
        onEmojiSelect={handleEmojiSelect}
      />
    </div>
  );
};

export default withStyles(styles)(EmojiReactions);
